import productsServiceCommon, {
  generateProppingImageResources,
  isType,
} from '../';
import constants from '../../../settings/constants';
import { getProppingBounds } from '../models';
import { FILTERS, ITEMS, MISSING_PRODUCT_CATEGORIES } from '../../../constants';
import { translate } from '../../L10n';
import { subFilterObject } from '../itemsFilters';
import { t } from '../../../translations';

const articleImages = [];

/**
 * See {@link getFilters} for docs on filters structure.
 * @type {[{filter: (function(*=): function({product: *}): boolean), condition: (function(): boolean), Component: () => JSX.Element}]}
 */
const getFilters = () => [subFilterObject({ appliesTo: [FILTERS.SECTIONS] })];

function getArticleImage(id) {
  return articleImages.find(image => image.name === id);
}

function getDragMode(product) {
  return constants.DRAG_MODE.FLOAT;
}

function getFit(item, slot) {
  let fits;

  switch (item.filter.type) {
    case ITEMS.SECTION:
      fits = productsServiceCommon.getFilteredItems(
        product => productsServiceCommon.isType(product, item.filter.type),
        {
          width: item.width,
          depth: item.depth,
          height: slot.height,
        }
      );
      break;
    case ITEMS.SHELF:
    case ITEMS.FELT_SHELF:
    case ITEMS.BOTTLE_RACK:
    case ITEMS.CABINET:
    case ITEMS.DRAWER:
    case ITEMS.TABLE:
      fits = productsServiceCommon.getFilteredItems(
        product => productsServiceCommon.isType(product, item.filter.type),
        {
          width: slot.width,
          depth: slot.depth,
          height: slot.height,
          color: slot.color || item.filter.color,
        }
      );
      break;
    default:
      return item;
  }

  if (fits?.length) {
    return Object.assign({}, fits[0]);
  }
}

const getColorSortValue = shelf => {
  if (!isType(shelf, 'shelf') || !shelf) return 0;
  const sortValue = {
    pine: 1,
    white: 2,
  };
  const shelfColor = shelf.filter.color;

  const sortValueKey = Object.keys(sortValue).find(key =>
    shelfColor.includes(key)
  );
  return sortValue[sortValueKey] || 0;
};

/**
 * Always sort in the following order if it exists on the market:
 * 1) Metal white
 * 2) Pine
 * @param {*} shelves
 */
const sortByColor = (shelf1, shelf2) => {
  return getColorSortValue(shelf1) - getColorSortValue(shelf2);
};

/**
 * Gets a matching shelf for a specific section
 * @param {*} section
 * @returns {Object | undefined}
 */
const getFittingShelf = section => {
  const WIDTH_DIFF = 60;

  const match = productsServiceCommon
    .getShelves({
      width: section.width - WIDTH_DIFF,
      depth: section.depth,
    })
    ?.sort(sortByColor)[0];

  return match ? { ...match } : null;
};

function makeArticleImages() {
  const imageList = productsServiceCommon.getAll().map(product => {
    const { color } = product.filter;
    const proppingBounds = getProppingBounds(product);

    const sceneFileName = `${product.modelid}_${color}_0001.png`;
    const menuFileName = `${product.modelid}_${color}_t_0001.png`;
    return {
      name: product.id,
      color: color,
      type: product.filter.type,
      modelid: product.modelid,
      proppingBounds,
      url: !productsServiceCommon.isSection(product)
        ? `${constants.IMAGE_ROOT}${sceneFileName}`
        : `${constants.IMAGE_ROOT}thumbnails/${menuFileName}`,
      thumbnailUrl: `${constants.IMAGE_ROOT}thumbnails/${menuFileName}`,
      thumbnailUrlRTL: `${constants.IMAGE_ROOT}thumbnails/rtl/${menuFileName}`,
      thumbnailUrlPortrait: `${constants.IMAGE_ROOT}thumbnails/portrait/${menuFileName}`,
    };
  });

  articleImages.push(...generateProppingImageResources(imageList));
}

/**
 * Translates key if id matches specific value
 * @param product {Object}
 * @returns {String}
 */
function getCustomArticleTypeText(product) {
  if (productsServiceCommon.isType(product.id, ITEMS.DOOR)) {
    return translate(t.MENU_PRODUCT_DOORS);
  }
}

function isInsert(item) {
  return productsServiceCommon.isType(item, [
    ITEMS.SHELF,
    ITEMS.FELT_SHELF,
    ITEMS.BOTTLE_RACK,
    ITEMS.CABINET,
    ITEMS.CHEST,
    ITEMS.TABLE,
    ITEMS.BOX,
    ITEMS.DOOR,
    ITEMS.DRAWER,
  ]);
}

function isSection(item) {
  return productsServiceCommon.isType(item, ITEMS.SECTION);
}

function getInitialZPos(item) {
  if (isSection(item)) {
    return 5;
  }
  return 0;
}

function shouldHaveColorSelector(item) {
  return productsServiceCommon.isType(item, [
    ITEMS.CABINET,
    ITEMS.SHELF_DRAWER,
  ]);
}

function shouldIgnoreFloorAndCeilingCollision(item) {
  return item.boundsType === 'space' && isInsert(item) && !item.items?.length;
}

function shouldShowDoorHint(item) {
  return productsServiceCommon.isType(item, ITEMS.DOOR);
}

function getMissingMandatoryProductCategories() {
  const sections = productsServiceCommon.getFilteredItems(item =>
    isSection(item)
  );
  return sections.length === 0 ? [MISSING_PRODUCT_CATEGORIES.SECTION] : [];
}

function showInfoIcon() {
  return true;
}

export default {
  articleImages,
  getArticleImage,
  getCustomArticleTypeText,
  getDragMode,
  getFit,
  getFittingShelf,
  getInitialZPos,
  isInsert,
  isSection,
  showInfoIcon,
  makeArticleImages,
  shouldHaveColorSelector,
  shouldIgnoreFloorAndCeilingCollision,
  shouldShowDoorHint,
  getMissingMandatoryProductCategories,
  getFilters,
};
